import * as Sentry from '@sentry/nextjs';
import React, { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { emailRegex } from '@winging/util/helper/regex';
import { useAuth } from '@/shared/providers/AuthProvider';
import { useToast } from '@/shared/providers/ToastProvider';
import { LabeledInputStatus } from '@/shared/ui/LabeledInput';
import { Button } from '@/shared/ui/Button';
import { checkExistingEmail } from '@/entities/user/api/user.service';
import SignUp from '@/features/auth/ui/SignUp';
import { validateSignUpForm } from '@/shared/lib/validation.util';

const LandingSignUp = () => {
  const t = useTranslations('Auth');
  const locale = useLocale();
  const { signUp } = useAuth();
  const { showToast } = useToast();
  const [passwordInputType, setPasswordInputType] = useState('password');
  const [passwordConfirmInputType, setPasswordConfirmInputType] =
    useState('password');

  const [email, setEmail] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailStatus, setEmailStatus] = useState<LabeledInputStatus>('');

  const [password, setPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordStatus, setPasswordStatus] = useState<LabeledInputStatus>('');

  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordConfirmMsg, setPasswordConfirmMsg] = useState('');
  const [passwordConfirmStatus, setPasswordConfirmStatus] =
    useState<LabeledInputStatus>('');

  const [term, setTerm] = useState(true);
  const [privacy, setPrivacy] = useState(true);
  const [marketing, setMarketing] = useState(true);

  const [duplicatedEmail, setDuplicatedEmail] = useState<null | boolean>(null);
  const ShowPasswordButton = (
    <button
      className={'main:right-[8px] relative right-[20px]'}
      onClick={() => {
        if (passwordInputType === 'password') {
          setPasswordInputType('text');
        } else {
          setPasswordInputType('password');
        }
      }}
    >
      <img
        src={
          passwordInputType === 'password'
            ? '/icons/hide-password.svg'
            : '/icons/show-password.svg'
        }
        alt={'show password button'}
      />
    </button>
  );

  const ShowPasswordConfirmButton = (
    <button
      className={'main:right-[8px] relative right-[20px]'}
      onClick={() => {
        if (passwordConfirmInputType === 'password') {
          setPasswordConfirmInputType('text');
        } else {
          setPasswordConfirmInputType('password');
        }
      }}
    >
      <img
        src={
          passwordInputType === 'password'
            ? '/icons/hide-password.svg'
            : '/icons/show-password.svg'
        }
        alt={'show password button'}
      />
    </button>
  );
  const CheckDuplicateEmailButton = (
    <Button
      onClick={async () => {
        if (!email || !emailRegex.test(email)) {
          setEmailMsg(t('이메일 형식으로 올바르게 입력해주세요'));
          setEmailStatus('error');
          return;
        }

        const res = await checkExistingEmail(email);
        setDuplicatedEmail(res);
        if (res) {
          setEmailMsg(t('이미 가입된 이메일입니다'));
          setEmailStatus('error');
        } else {
          setEmailMsg(t('사용 가능한 이메일입니다'));
          setEmailStatus('');
        }
      }}
      disabled={duplicatedEmail === false}
      size={'inline-medium'}
      style={{
        fontSize: '13px',
        fontWeight: 400,
        width: locale === 'ko' ? '86px' : '120px',
      }}
      className={'main:h-[40px] h-[46px]'}
    >
      {t('중복 확인')}
    </Button>
  );
  const validate = (): boolean => {
    return validateSignUpForm(
      {
        email,
        password,
        passwordConfirm,
        duplicatedEmail,
      },
      {
        setEmailStatus,
        setEmailMsg,
        setPasswordStatus,
        setPasswordMsg,
        setPasswordConfirmStatus,
        setPasswordConfirmMsg,
      },
      {
        emailRequired: t('이메일 주소가 입력되지 않았습니다'),
        emailInvalid: t('이메일 형식으로 올바르게 입력해주세요'),
        emailDuplicated: t('이미 사용 중인 이메일입니다'),
        passwordRequired: t('비밀번호가 입력되지 않았습니다'),
        passwordInvalid: t('8자리 이상으로 설정해 주세요'),
        passwordConfirmRequired: t('비밀번호를 한번 더 입력해주세요'),
        passwordMismatch: t('비밀번호와 비밀번호 확인이 일치하지 않습니다'),
      }
    );
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }
    try {
      await signUp({
        email,
        password,
        locale,
        is_marketing_agreed: marketing ? new Date(Date.now()) : null,
      });
    } catch (e) {
      Sentry.captureException(e);
      showToast(t('알 수 없는 에러가 발생했습니다'));
    }
  };
  return (
    <SignUp
      emailStatus={emailStatus}
      email={email}
      emailMsg={emailMsg}
      setEmail={setEmail}
      setEmailStatus={setEmailStatus}
      setEmailMsg={setEmailMsg}
      setDuplicatedEmail={setDuplicatedEmail}
      CheckDuplicateEmailButton={CheckDuplicateEmailButton}
      passwordStatus={passwordStatus}
      password={password}
      passwordMsg={passwordMsg}
      setPassword={setPassword}
      setPasswordStatus={setPasswordStatus}
      setPasswordMsg={setPasswordMsg}
      passwordInputType={passwordInputType}
      ShowPasswordButton={ShowPasswordButton}
      passwordConfirmStatus={passwordConfirmStatus}
      passwordConfirm={passwordConfirm}
      passwordConfirmMsg={passwordConfirmMsg}
      passwordConfirmInputType={passwordConfirmInputType}
      setPasswordConfirm={setPasswordConfirm}
      setPasswordConfirmStatus={setPasswordConfirmStatus}
      setPasswordConfirmMsg={setPasswordConfirmMsg}
      ShowPasswordConfirmButton={ShowPasswordConfirmButton}
      term={term}
      setTerm={setTerm}
      privacy={privacy}
      setPrivacy={setPrivacy}
      setMarketing={setMarketing}
      marketing={marketing}
      submit={submit}
      duplicatedEmail={duplicatedEmail}
    />
  );
};

export default LandingSignUp;
